import { useMutation, useQuery } from 'react-query';
import { useClient } from '../../api/api-client';

export const permissionsKey = ['permissions'];
export const lifelineKey = ['lifeline'];
export const agentKey = ['agent'];
export const aadActionKey = ['aad_action'];

export function useGetConfig(queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...permissionsKey, 'config'],
    queryFn: () => client('permissions/config'),
    ...queryConfig,
  });
}

export function useCreateUpdateAgent(slug, mutationConfig = {}) {
  const client = useClient();

  return useMutation({
    mutationKey: [...permissionsKey, 'create_update_agent'],
    mutationFn: (data) => client(`lifeline_apis/auth/${slug}`, { data }),
    ...mutationConfig,
  });
}

export function useGetBlockDeviceReasonCodes(queryConfig) {
  const client = useClient();

  return useQuery({
    queryKey: [...permissionsKey, 'dashboards'],
    queryFn: () => client(`lifeline_apis/auth/get_device_blocking_reasons`),
    ...queryConfig,
  });
}

export function useBlockUnBlockUser(slug, mutationConfig = {}) {
  const client = useClient();

  return useMutation({
    mutationKey: [...permissionsKey, 'block_unblock_agent'],
    mutationFn: (data) => client(`lifeline_apis/auth/${slug}`, { data }),
    ...mutationConfig,
  });
}

export function useGetDashboardPermissions(slug, queryConfig) {
  const client = useClient();

  return useQuery({
    queryKey: [...permissionsKey, 'dashboards', slug],
    queryFn: () => client(`permissions/dashboards/${slug}`),
    enabled: Boolean(slug),
    ...queryConfig,
  });
}

export function useGetDashboardAllPermissions(slug, queryConfig) {
  const client = useClient();

  return useQuery({
    queryKey: [...permissionsKey, 'dashboard', slug],
    queryFn: () =>
      client(
        `permissions/dashboard/fetch_all_permissions?dasboard_slug=${slug}`,
      ),
    enabled: Boolean(slug),
    ...queryConfig,
  });
}

export function useGetAllOrganisations(queryConfig) {
  const client = useClient();

  return useQuery({
    queryKey: ['useGetAllOrganisations'],
    queryFn: () => client('dashboard/get_all_organisations/'),
    enabled: true,
    ...queryConfig,
  });
}

export function useGetDashboards(queryConfig = {}) {
  const client = useClient();

  return useQuery({
    queryKey: [...permissionsKey, 'dashboards'],
    queryFn: () => client('permissions/dashboards'),
    ...queryConfig,
  });
}

export function useGetRevokeReasons() {
  const client = useClient();
  return useQuery({
    queryKey: [...permissionsKey, 'permission_updates/reasons'],
    queryFn: () => client('permissions/permission_updates/reasons'),
  });
}

export function useGrantAndRevoke(queryConfig) {
  const client = useClient();

  return useMutation(
    (data) => client('permissions/user_permissions', { data }),
    {
      mutationKey: [...permissionsKey, 'user_permissions'],
      ...queryConfig,
    },
  );
}
export function useGetUserPermissionsHistory(email, queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...permissionsKey, 'permission_updates'],
    queryFn: () => client(`permissions/permission_updates?email=${email}`),
    enabled: false,
    ...queryConfig,
  });
}

export function useGetUserPermissions(email, queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...permissionsKey, 'user_permissions'],
    queryFn: () => client(`permissions/user_permissions?email=${email}`),
    enabled: false,
    ...queryConfig,
  });
}

export function useUpdateCustomerEmail(mutationConfig = {}) {
  const client = useClient();
  return useMutation({
    mutationKey: [...permissionsKey, 'create_update_agent'],
    mutationFn: (data) =>
      client(`lifeline_apis/auth/cx_email_update`, { data }),
    ...mutationConfig,
  });
}

export function useGetUserData(userId) {
  const client = useClient();
  return useQuery({
    queryKey: [...lifelineKey, 'get_user_info', userId],
    enabled: Boolean(userId),
    queryFn: () =>
      client(`lifeline_apis/user/get_basic_info?user_id=${userId}`),
  });
}

export function useGetKarmaOptions() {
  const client = useClient();
  return useQuery({
    queryKey: [...lifelineKey, 'force_update_user_karma', 'get_scores'],
    queryFn: () => client(`permissions/force_update_user_karma/get_scores/`),
  });
}

export function useGetKarmaUpdateReason(userId, karmaScore) {
  const client = useClient();
  return useQuery({
    queryKey: [
      ...lifelineKey,
      'user_id',
      'force_update_user_karma',
      'reason_codes',
      userId,
      karmaScore,
    ],
    enabled: Boolean(userId && karmaScore),
    queryFn: () =>
      client(
        `permissions/force_update_user_karma/reason_codes/?user_id=${userId}&updated_karma_score=${karmaScore}`,
      ),
  });
}

export function useForceUpdateKarma(mutationConfig) {
  const client = useClient();

  return useMutation({
    mutationKey: [...permissionsKey, 'karma'],
    mutationFn: (data) =>
      client(`permissions/force_update_user_karma/update/`, { data }),
    ...mutationConfig,
  });
}

export function useTestTatPolicyFields(config = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...lifelineKey, 'admin', 'test_tat_policy'],
    queryFn: () => client(`admin/test_tat_policy`),
    ...config,
  });
}

export function useEvaluateTatPolicy(mutationConfig = {}) {
  const client = useClient();
  return useMutation({
    mutationKey: [...lifelineKey, 'admin', 'test_tat_policy'],
    mutationFn: (data) => client(`admin/test_tat_policy`, { data }),
    ...mutationConfig,
  });
}

export function useGetOdpFields(promoType, config = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...lifelineKey, 'admin', 'create_odp', promoType],
    queryFn: () => client(`admin/create_odp?promo_type=${promoType}`),
    ...config,
  });
}

export function useCreateOdp(mutationConfig = {}) {
  const client = useClient();

  return useMutation({
    mutationKey: [...permissionsKey, 'admin', 'create_odp'],
    mutationFn: (data) => client(`admin/create_odp`, { data }),
    ...mutationConfig,
  });
}

export function useUpdateAgentEmail(mutationConfig = {}) {
  const client = useClient();
  return useMutation({
    mutationKey: [...permissionsKey, 'migrate_mapping'],
    mutationFn: (data) =>
      client(`lifeline_apis/auth/migrate_mapping`, { data }),
    ...mutationConfig,
  });
}

export function useGetAgentLimitForm(queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...agentKey, 'get_agent_limit'],
    queryFn: () => client(`agent/get_limit_form`),
    ...queryConfig,
  });
}

export function useGetResolutionLimitOptions(agentEmail, queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...agentKey, agentEmail, 'resolution_limit'],
    queryFn: () =>
      client(`agent/get_resolution_limit?agent_email=${agentEmail}`),
    ...queryConfig,
    enabled: false,
    cacheTime: 0,
  });
}

export function useUpdateAgentLimit(mutationConfig = {}) {
  const client = useClient();
  return useMutation({
    mutationKey: [...agentKey, 'set_agent_limit'],
    mutationFn: (data) => client(`agent/set_limit`, { data }),
    ...mutationConfig,
  });
}

export function useUpdateInvoice(mutationConfig = {}) {
  const client = useClient();
  return useMutation({
    mutationKey: ['update_invoice'],
    mutationFn: (data) => client(`admin/update_invoice`, { data }),
    ...mutationConfig,
  });
}

export function useGetAADFeatures(queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...aadActionKey, 'features'],
    queryFn: () => client('admin/common/features'),
    ...queryConfig,
  });
}

export function useGetAADOperations(queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...aadActionKey, 'operations'],
    queryFn: () => client('admin/common/operations'),
    ...queryConfig,
  });
}

export function useGetAADComponents(queryConfig = {}) {
  const client = useClient();
  return useQuery({
    queryKey: [...aadActionKey, 'components'],
    queryFn: () => client('admin/common/components'),
    enabled: false,
    ...queryConfig,
  });
}

export function useSubmitAADAction(mutationConfig = {}) {
  const client = useClient();
  return useMutation({
    mutationKey: [...aadActionKey, 'submit'],
    mutationFn: ({ targetEndpoint, method, data }) =>
      client(targetEndpoint.replace(/^\//, ''), { data, method }),
    meta: { hideAlert: true },
    ...mutationConfig,
  });
}
